/**
 * AuthGate — /auth-gate
 *
 * Theme-matched login/signup gate. Shown when:
 *   • storefrontAuthRequired === true AND mode === "hard" AND no session
 *   • Or when a soft-gate triggers Buy intent and user is not signed in
 *
 * Honors active storefront theme (classic/modern/minimal/lite) by applying
 * the matching theme-* wrapper class. Preserves ?ref= and a returnTo path so
 * referral context and deep links survive sign-in.
 *
 * Uses Supabase auth directly (signInWithPassword + signUp + Google OAuth),
 * with a Remember me toggle that mirrors LiteAuthSheet's behavior.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { useAuth } from "@/hooks/useAuth";
import { useStorefrontTheme } from "@/stores/storefrontThemeStore";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
// adminSettings reserved for future branding additions
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle, CheckCircle2, Eye, EyeOff, ShieldCheck, ArrowLeft, Lock, UserRound } from "lucide-react";
import { grantGuestPass } from "@/lib/guestPass";
import { z } from "zod";
import { toast } from "sonner";
import CustomerOtpGate from "@/components/CustomerOtpGate";
import { requestLoginContextChoice } from "@/stores/loginContextChoiceStore";
import { persistSignupAttribution } from "@/data/signupAttribution";
import { logCustomerActivity } from "@/data/customerActivity";
import { buildSignupRedirectUrl } from "@/lib/signupRedirect";

const ADMIN_ROLES = new Set(["super_admin", "admin", "manager", "staff"]);
const PENDING_VERIFY_KEY = "khays_pending_signup_verify";
const REMEMBER_KEY = "khays_lite_remember_me";
const SESSION_ONLY_KEY = "khays_lite_session_active";
const GATE_RETURN_KEY = "khays_auth_gate_return";

const signinSchema = z.object({
  email: z.string().trim().email({ message: "Enter a valid email address." }).max(254),
  password: z.string().min(6, { message: "Password must be at least 6 characters." }).max(128),
});
const signupSchema = signinSchema.extend({
  password: z.string().min(8, { message: "Use at least 8 characters for new accounts." }).max(128),
});

function friendlyError(raw: string | undefined, mode: "signin" | "signup" | "forgot"): string {
  const msg = (raw || "").toLowerCase();
  if (!msg) return "Something went wrong. Please try again.";
  if (msg.includes("invalid login") || msg.includes("invalid_credentials"))
    return "Wrong email or password. Please check and try again.";
  if (msg.includes("email not confirmed"))
    return "Please confirm your email — we sent you a verification link.";
  if (msg.includes("user already registered") || msg.includes("already exists"))
    return "An account with this email already exists. Try signing in instead.";
  if (msg.includes("rate limit") || msg.includes("too many"))
    return "Too many attempts. Please wait a minute and try again.";
  if (msg.includes("network") || msg.includes("fetch"))
    return "Network problem. Check your connection and try again.";
  if (msg.includes("popup") || msg.includes("blocked"))
    return "Pop-up blocked. Please allow pop-ups for Google sign-in.";
  return mode === "signin"
    ? "Sign-in failed. Please double-check your details."
    : "Sign-up failed. Please try again.";
}

async function fetchRole(userId: string): Promise<string | null> {
  try {
    const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId).maybeSingle();
    return (data?.role as string) || null;
  } catch { return null; }
}

export function setAuthGateReturn(path: string): void {
  try { sessionStorage.setItem(GATE_RETURN_KEY, path); } catch {}
}

export default function AuthGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const adminDefault = useStorefrontTheme(s => s.adminDefault);
  const visitorTheme = useStorefrontTheme(s => s.visitorTheme);
  const visitorToggleEnabled = useStorefrontTheme(s => s.visitorToggleEnabled);
  const activeTheme = visitorToggleEnabled && visitorTheme ? visitorTheme : adminDefault;
  const sfSettings = useStorefrontSettings();
  // (no admin settings used in branding for now)
  const allowRemember = sfSettings.storefrontAuthAllowRemember !== false;

  const themeClass = useMemo(() => {
    if (activeTheme === "lite") return "theme-lite";
    if (activeTheme === "modern") return "theme-modern";
    if (activeTheme === "minimal") return "theme-minimal";
    return "theme-classic";
  }, [activeTheme]);

  // ─── Determine return-to path ──────────────────────────────
  const returnTo = useMemo(() => {
    const fromQuery = searchParams.get("returnTo");
    if (fromQuery) return fromQuery;
    try {
      const stored = sessionStorage.getItem(GATE_RETURN_KEY);
      if (stored) return stored;
    } catch {}
    return "/";
  }, [searchParams]);

  // Preserve ?ref= so referral context survives sign-in
  const ref = searchParams.get("ref");

  // ─── Auto-bypass when already signed in ────────────────────
  useEffect(() => {
    if (user) {
      try { sessionStorage.removeItem(GATE_RETURN_KEY); } catch {}
      const target = ref && !returnTo.includes("ref=") ? `${returnTo}${returnTo.includes("?") ? "&" : "?"}ref=${encodeURIComponent(ref)}` : returnTo;
      if (sfSettings.storefrontAuthShowWelcomeBack !== false) {
        let isNew = false;
        try { isNew = sessionStorage.getItem("khays_just_signed_up") === "1"; } catch {}
        try { sessionStorage.removeItem("khays_just_signed_up"); } catch {}
        const name = (user.user_metadata?.full_name as string | undefined)?.split(" ")[0] || "";
        if (isNew) {
          toast.success(name ? `Welcome, ${name}!` : "Welcome!");
        } else {
          toast.success(name ? `Welcome back, ${name}!` : "Welcome back!");
        }
      }
      navigate(target, { replace: true });
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const [mode, setMode] = useState<"signin" | "signup" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [gender, setGender] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [topError, setTopError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [needsVerification, setNeedsVerification] = useState(false);
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState<string | null>(null);
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(REMEMBER_KEY);
      if (v != null) setRemember(v === "1");
    } catch {}
    try {
      const v = localStorage.getItem(PENDING_VERIFY_KEY);
      if (v) setPendingVerifyEmail(v);
    } catch {}
  }, []);

  useEffect(() => { setFieldErrors({}); setTopError(null); setNeedsVerification(false); }, [mode]);

  const persistRemember = (v: boolean) => {
    setRemember(v);
    try { localStorage.setItem(REMEMBER_KEY, v ? "1" : "0"); } catch {}
  };

  const markSessionScope = () => {
    try {
      if (allowRemember && !remember) sessionStorage.setItem(SESSION_ONLY_KEY, "1");
      else sessionStorage.removeItem(SESSION_ONLY_KEY);
    } catch {}
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    const schema = mode === "signin" ? signinSchema : signupSchema;
    const r = schema.safeParse({ email: email.trim(), password });
    if (!r.success) {
      for (const issue of r.error.issues) {
        const f = issue.path[0] as string;
        if (!errs[f]) errs[f] = issue.message;
      }
    }
    if (mode === "signup") {
      if (!fullName.trim() || fullName.trim().length < 2) errs.fullName = "Please enter your full name (2+ characters).";
      const cleanPhone = phone.replace(/[\s-]/g, "");
      if (!/^\+?\d{7,15}$/.test(cleanPhone)) errs.phone = "Enter a valid phone number (7–15 digits).";
      if (!city.trim() || city.trim().length < 2) errs.city = "Please enter your city or location.";
      if (gender !== "male" && gender !== "female") errs.gender = "Please select Male or Female.";
      if (password && confirmPassword !== password) errs.confirmPassword = "Passwords do not match.";
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const finishAuth = async (userId: string | undefined) => {
    if (!userId) return;
    // Best-effort: attach captured ?ref/?staff/?campaign/utm_* to the new
    // customer's prefs so admins can see the signup source.
    try { await persistSignupAttribution(userId); } catch {}
    try { logCustomerActivity(mode === "signup" ? "signup" : "login", { method: "email" }); } catch {}
    const role = await fetchRole(userId);
    if (role && ADMIN_ROLES.has(role)) {
      requestLoginContextChoice({ userId, role });
      return; // user choice modal handles routing
    }
    // Bypass effect will navigate via the user-watching effect on next render.
  };

  const handleEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setTopError(null);
    setNeedsVerification(false);
    if (!validate()) return;
    setBusy(true);
    try {
      if (mode === "signin") {
        const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
        if (error) {
          const m = (error.message || "").toLowerCase();
          if (m.includes("email not confirmed") || m.includes("email_not_confirmed")) {
            setNeedsVerification(true);
            setTopError("Please verify your email before signing in. Check your inbox.");
            return;
          }
          throw error;
        }
        if (data.user && !data.user.email_confirmed_at && !(data.user as any).confirmed_at) {
          await supabase.auth.signOut();
          setNeedsVerification(true);
          setTopError("Please verify your email before signing in.");
          return;
        }
        markSessionScope();
        await finishAuth(data.user?.id);
      } else {
        const { data, error } = await supabase.auth.signUp({
          email: email.trim(), password,
          options: {
            emailRedirectTo: buildSignupRedirectUrl(),
            data: {
              account_type: "customer",
              full_name: fullName.trim(),
              phone_number: phone.trim(),
              location: city.trim(),
              gender: gender || null,
              signup_tenant_id: (await import("@/lib/tenantContext")).getCurrentTenantId() || null,
            },
          },
        });
        if (error) {
          const m = (error.message || "").toLowerCase();
          if (m.includes("already") || m.includes("registered") || m.includes("exists")) {
            setTopError("This email is already registered. Please sign in with your existing password.");
            setMode("signin");
            return;
          }
          throw error;
        }
        if (!data.session) {
          const verifyEmail = email.trim();
          try { localStorage.setItem(PENDING_VERIFY_KEY, verifyEmail); } catch {}
          setPendingVerifyEmail(verifyEmail);
          toast.success("Check your email", { description: "We sent a verification code to verify your account." });
        } else {
          if (gender && data.user?.id) {
            try {
              await supabase.from("customer_prefs").upsert(
                { customer_user_id: data.user.id, data: { gender } as any },
                { onConflict: "customer_user_id" }
              );
            } catch {}
          }
          try { sessionStorage.setItem("khays_just_signed_up", "1"); } catch {}
          markSessionScope();
          await finishAuth(data.user?.id);
        }
      }
    } catch (err: any) {
      setTopError(friendlyError(err?.message, mode));
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setTopError(null);
    setBusy(true);
    try {
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}${location.pathname}${location.search}`,
      });
      if (result.error) throw result.error;
      markSessionScope();
      if (!result.redirected) {
        const { data: sess } = await supabase.auth.getSession();
        await finishAuth(sess.session?.user?.id);
      }
    } catch (err: any) {
      setTopError(friendlyError(err?.message, mode));
    } finally { setBusy(false); }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setTopError(null); setFieldErrors({});
    const r = z.string().trim().email().safeParse(email.trim());
    if (!r.success) { setFieldErrors({ email: "Enter a valid email address." }); return; }
    setBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      setForgotSent(true);
      toast.success("Reset link sent", { description: "Check your inbox (and spam folder)." });
    } catch (err: any) {
      setTopError(friendlyError(err?.message, "forgot"));
    } finally { setBusy(false); }
  };

  // ─── Verification (OTP) view ───
  if (pendingVerifyEmail) {
    return (
      <div className={`${themeClass} min-h-[100dvh] bg-background text-foreground flex items-center justify-center p-4`}>
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-xl">
          <h1 className="font-display text-xl font-extrabold mb-1">Verify your email</h1>
          <p className="text-xs text-muted-foreground mb-4">Enter the verification code we just emailed you.</p>
          <CustomerOtpGate
            email={pendingVerifyEmail}
            onVerified={async (uid) => {
              try { localStorage.removeItem(PENDING_VERIFY_KEY); } catch {}
              setPendingVerifyEmail(null);
              if (gender && uid) {
                try {
                  await supabase.from("customer_prefs").upsert(
                    { customer_user_id: uid, data: { gender } as any },
                    { onConflict: "customer_user_id" }
                  );
                } catch {}
              }
              try { sessionStorage.setItem("khays_just_signed_up", "1"); } catch {}
              markSessionScope();
              await finishAuth(uid);
            }}
            onCancel={() => {
              try { localStorage.removeItem(PENDING_VERIFY_KEY); } catch {}
              setPendingVerifyEmail(null);
              setMode("signup");
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={`${themeClass} min-h-[100dvh] bg-background text-foreground flex flex-col items-center justify-center p-4`}>
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/10 text-primary mb-3">
            <Lock className="w-6 h-6" />
          </div>
          <h1 className="font-display text-2xl font-extrabold">Welcome</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {mode === "signin" ? "Sign in to continue to the storefront." :
             mode === "signup" ? "Create an account to start shopping." :
             "Reset your password."}
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5 shadow-xl space-y-4">
          {topError && (
            <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-2.5 text-xs font-semibold text-destructive">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{topError}</span>
            </div>
          )}

          {searchParams.get("intent") === "buy" && (
            <div className="flex items-start gap-2 rounded-lg border border-primary/40 bg-primary/10 p-2.5 text-xs font-semibold text-primary">
              <Lock className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Please sign in to complete your purchase — we'll take you right back.</span>
            </div>
          )}

          {needsVerification && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
              <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
              <span>Check your inbox (and spam) for the verification link.</span>
            </div>
          )}

          {mode !== "forgot" && sfSettings.googleAuthEnabled !== false && (
            <>
              <Button type="button" variant="outline" className="w-full h-11 font-bold" onClick={handleGoogle} disabled={busy}>
                <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.07 5.07 0 0 1-2.2 3.32v2.76h3.55c2.08-1.91 3.29-4.74 3.29-8.09Z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.99.66-2.25 1.06-3.73 1.06-2.87 0-5.3-1.94-6.17-4.55H2.17v2.85A11 11 0 0 0 12 23Z"/>
                  <path fill="#FBBC05" d="M5.83 14.09a6.6 6.6 0 0 1 0-4.18V7.06H2.17a11 11 0 0 0 0 9.88l3.66-2.85Z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.17 7.06l3.66 2.85C6.7 7.31 9.13 5.38 12 5.38Z"/>
                </svg>
                Continue with Google
              </Button>
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
            </>
          )}

          {mode === "forgot" ? (
            <form onSubmit={handleForgot} className="space-y-3" noValidate>
              <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} autoComplete="email"
                aria-invalid={!!fieldErrors.email}
                className={fieldErrors.email ? "border-destructive" : ""} />
              {fieldErrors.email && <p className="text-[11px] text-destructive font-semibold">{fieldErrors.email}</p>}
              {forgotSent && (
                <div className="flex items-start gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-2.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>If an account exists for that email, a reset link is on its way.</span>
                </div>
              )}
              <Button type="submit" className="w-full h-11 font-bold" disabled={busy}>
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
              </Button>
              <button type="button" onClick={() => { setMode("signin"); setForgotSent(false); }} className="w-full text-center text-sm text-muted-foreground hover:text-foreground" disabled={busy}>
                Back to <span className="font-bold text-primary">Sign in</span>
              </button>
            </form>
          ) : (
            <>
              <form onSubmit={handleEmail} className="space-y-3" noValidate>
                <div className="space-y-1">
                  <Input type="email" placeholder="Email" value={email} onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors(p => ({ ...p, email: "" })); }}
                    disabled={busy} autoComplete="email" aria-invalid={!!fieldErrors.email}
                    className={fieldErrors.email ? "border-destructive" : ""} />
                  {fieldErrors.email && <p className="text-[11px] text-destructive font-semibold">{fieldErrors.email}</p>}
                </div>
                <div className="space-y-1">
                  <div className="relative">
                    <Input type={showPassword ? "text" : "password"}
                      placeholder={mode === "signin" ? "Password" : "Password (min 8 chars)"}
                      value={password} onChange={(e) => { setPassword(e.target.value); if (fieldErrors.password) setFieldErrors(p => ({ ...p, password: "" })); }}
                      disabled={busy} autoComplete={mode === "signin" ? "current-password" : "new-password"}
                      aria-invalid={!!fieldErrors.password}
                      className={`pr-10 ${fieldErrors.password ? "border-destructive" : ""}`} />
                    <button type="button" onClick={() => setShowPassword(s => !s)} aria-label={showPassword ? "Hide password" : "Show password"} tabIndex={-1}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {fieldErrors.password && <p className="text-[11px] text-destructive font-semibold">{fieldErrors.password}</p>}
                </div>

                {mode === "signup" && (
                  <>
                    <Input type={showPassword ? "text" : "password"} placeholder="Confirm password *" value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)} disabled={busy} autoComplete="new-password"
                      className={fieldErrors.confirmPassword ? "border-destructive" : ""} />
                    {fieldErrors.confirmPassword && <p className="text-[11px] text-destructive font-semibold">{fieldErrors.confirmPassword}</p>}
                    <Input type="text" placeholder="Full name *" value={fullName} onChange={(e) => setFullName(e.target.value)} disabled={busy} autoComplete="name" maxLength={80}
                      className={fieldErrors.fullName ? "border-destructive" : ""} />
                    {fieldErrors.fullName && <p className="text-[11px] text-destructive font-semibold">{fieldErrors.fullName}</p>}
                    <Input type="tel" inputMode="tel" placeholder="Phone number *" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={busy} autoComplete="tel" maxLength={20}
                      className={fieldErrors.phone ? "border-destructive" : ""} />
                    {fieldErrors.phone && <p className="text-[11px] text-destructive font-semibold">{fieldErrors.phone}</p>}
                    <Input type="text" placeholder="City / location *" value={city} onChange={(e) => setCity(e.target.value)} disabled={busy} autoComplete="address-level2" maxLength={80}
                      className={fieldErrors.city ? "border-destructive" : ""} />
                    {fieldErrors.city && <p className="text-[11px] text-destructive font-semibold">{fieldErrors.city}</p>}
                    <select value={gender} onChange={(e) => setGender(e.target.value)} disabled={busy} aria-label="Gender"
                      className={`w-full h-10 rounded-md border bg-background px-3 text-sm ${fieldErrors.gender ? "border-destructive" : "border-input"}`}>
                      <option value="">Gender *</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                    {fieldErrors.gender && <p className="text-[11px] text-destructive font-semibold">{fieldErrors.gender}</p>}
                  </>
                )}

                {mode === "signin" && (
                  <div className="flex items-center justify-between gap-3">
                    {allowRemember ? (
                      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                        <input type="checkbox" checked={remember} onChange={(e) => persistRemember(e.target.checked)} disabled={busy} className="w-4 h-4 rounded border-border accent-primary" />
                        Keep me logged in
                      </label>
                    ) : <span />}
                    <button type="button" onClick={() => setMode("forgot")} className="text-xs font-bold text-primary hover:underline" disabled={busy}>
                      Forgot password?
                    </button>
                  </div>
                )}

                <Button type="submit" className="w-full h-11 font-bold" disabled={busy}>
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "signin" ? "Sign in" : "Create account")}
                </Button>
              </form>

              <button type="button" onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground" disabled={busy}>
                {mode === "signin" ? <>Don't have an account? <span className="font-bold text-primary">Create one</span></>
                  : <>Already have an account? <span className="font-bold text-primary">Sign in</span></>}
              </button>
            </>
          )}

          {mode !== "forgot" && sfSettings.storefrontAuthMode !== "soft" && searchParams.get("intent") !== "buy" && (
            <>
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <Button type="button" variant="secondary" disabled={busy}
                className="w-full h-11 font-bold border border-primary/30"
                onClick={() => {
                  grantGuestPass();
                  try { sessionStorage.removeItem(GATE_RETURN_KEY); } catch {}
                  toast.success("Browsing as a guest", { description: "You'll be asked to sign in when you check out." });
                  window.location.replace(returnTo && returnTo !== "/auth-gate" ? returnTo : "/");
                }}>
                <UserRound className="w-4 h-4 mr-2" /> Continue as Guest — sign up later
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">
                Browse the whole store now. You'll only need an account when you place an order.
              </p>
            </>
          )}
        </div>

        {sfSettings.storefrontAuthMode === "soft" && (
          <button type="button" onClick={() => navigate(returnTo, { replace: true })}
            className="mt-4 mx-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-3.5 h-3.5" /> Continue browsing
          </button>
        )}
      </div>
    </div>
  );
}
